import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import { Activity } from "$types/Activity";
import useCompany from "$hooks/useCompany";
import { Company, CompanyType } from "$types";
import { ActivitiesProps } from "../sections/Activities";

export default function useActivities({
  companyTypes,
  relatedCarrierId,
  defaultValues,
  companies,
}: ActivitiesProps) {
  const { data: activities = [] } = useActivitiesQuery();
  const { isAgency, isCarrier, isLogistician } = useCompany();
  const { trigger, getValues } = useFormContext();
  const [relatedCarrierActivities, setRelatedCarrierActivities] = useState<
    Activity[]
  >([]);

  const carrierActivities = useMemo(
    () => activities?.filter((a) => a.type === CompanyType.CARRIER) || [],
    [activities]
  );

  const logisticianActivities = useMemo(
    () => activities?.filter((a) => a.type === CompanyType.LOGISTICIAN) || [],
    [activities]
  );

  const usedCarrierActivities = useMemo(() => {
    if (!isCarrier(defaultValues?.companyTypes) || !defaultValues._id) {
      return [];
    }

    const agencies = companies.filter(
      (c: Company) => c.agencyOf === defaultValues?._id
    );
    const usedActivities: string[] = [];

    let i = 0;
    while (
      usedActivities.length !== carrierActivities.length &&
      i !== agencies.length
    ) {
      const agency = agencies[i];
      agency.activities.forEach((a: Activity | string) => {
        // type is activity or string :)
        if (typeof a === "string" && !usedActivities.includes(a)) {
          usedActivities.push(a);
        }
      });
      i += 1;
    }

    return usedActivities;
  }, [
    carrierActivities.length,
    companies,
    defaultValues?._id,
    defaultValues?.companyTypes,
    isCarrier,
  ]);

  // update available activities for an agency using its related carrier
  useEffect(() => {
    const computeAvailableActivities = () => {
      if (!relatedCarrierId) return;

      const carrier = companies.find(
        (c: Company) => c._id === relatedCarrierId
      );
      const carrierActivityIds = carrier?.activities || [];
      const availableActivities = activities.filter((activity: Activity) =>
        // nah, would be too easy :)
        // carrierActivityIds.includes(activity._id)
        carrierActivityIds.some((carrierActivity: Activity | string) =>
          typeof carrierActivity === "string"
            ? carrierActivity === activity._id
            : carrierActivity._id === activity._id
        )
      );

      setRelatedCarrierActivities(availableActivities);
      if (getValues("name")) {
        trigger("name"); // force check the name for existing agency
      }
    };
    computeAvailableActivities();
  }, [activities, companies, getValues, relatedCarrierId, trigger]);

  const getRelevantActivities = () => {
    if (isAgency(companyTypes)) {
      return relatedCarrierActivities;
    }
    if (isLogistician(companyTypes)) {
      return logisticianActivities;
    }
    if (isCarrier(companyTypes)) {
      return carrierActivities;
    }
    return [];
  };

  return {
    activities,
    relatedCarrierActivities,
    setRelatedCarrierActivities,
    carrierActivities,
    logisticianActivities,
    usedCarrierActivities,
    getRelevantActivities,
  };
}
